/* eslint-disable max-lines */
import { useMemoizedFn } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  Collapse,
  Drawer,
  Input,
  InputNumber,
  Link,
  Loading,
  MessagePlugin,
  Select,
  Switch,
  Tag,
} from 'tdesign-react';
import Descriptions from '@/components/Descriptions';
import { isValidJSONRC, isValidSimpleJSONRC, isVoid, toTsOptions } from '@/utils/common';
import JSONConverterInput from '@/components/Form/JSONConverterInput';
import Tip from '@/components/Tip';
import JSONsInput, { isValidSubJSONRC } from '@/components/Form/JSONsInput';
import Form from '@/components/Form';
import SvgIcon from '@/components/SvgIcon';
import Text from '@/components/Text';
import DisableWrapper from '@/components/DisableWrapper';
import { checkFunctionAuthRole } from '@/services/functionService';
import YamlToJson from './YamlToJson';
import Auth from './Auth';

const { FormItem } = Form;
const { Panel } = Collapse;

// 日志配置转换
const getLogConfigString = (config) => {
  const { name, key } = config || {};
  if (isVoid(name) && isVoid(key)) {
    return '';
  }
  return JSON.stringify(config);
};

const othersK8sTabs = [
  {
    label: '亲和性',
    value: 'podAffinity',
    tip: 'Pod 的节点亲和性/反亲和性配置',
    placeholder: `参考示例如下:
{
  "nodeAffinity": {
    "requiredDuringSchedulingIgnoredDuringExecution": {
      "nodeSelectorTerms": [{
        "matchExpressions": [{
          "key": "pulsar/function",
          "operator": "In",
          "values": ["true"]
        }]
      }]
    }
  },
  "podAffinity": {
    "preferredDuringSchedulingIgnoredDuringExecution": [{
      "podAffinityTerm": {
        "labelSelector": {
          "matchLabels": {
            "foo": "bar"
          }
        },
        "namespaces": ["default"],
        "topologyKey": "kubernetes.io/hostname"
      },
      "weight": 10
    }]
  },
  "podAntiAffinity": {
    "requiredDuringSchedulingIgnoredDuringExecution": [{
      "labelSelector": {
        "matchExpressions": [{
          "key": "name",
          "operator": "In",
          "values": ["pulsar-function-1"]
        }]
      },
      "topologyKey": "kubernetes.io/hostname"
    }]
  }
}
  `,
  },
  {
    label: '指定调度',
    value: 'nodeSelector',
    tip: 'Pod 的nodeSelector配置，可以让你的pod调度到指定的node节点上运行',
    placeholder: `参考示例如下:
{
  "nodeSelector": {
    "disktype": "ssd",
    "...": "...."
  }
}
  `,
  },
  {
    label: '污点和容忍度',
    value: 'tolerations',
    tip: 'Pod 的污点和容忍度配置',
    placeholder: `参考示例如下:
{
  "tolerations": [
    {
      "effect": "NoSchedule",
      "key": "test-toleration-key",
      "operator": "Equal",
      "value": "test-toleration-value"
    }
  ]
}
  `,
  },
  {
    label: '存储挂载',
    value: 'functionSpecVolumeClaimTemplates',
    tip: 'Pod 的存储挂载配置',
    placeholder: `参考示例如下:
{
  "functionSpecVolumeClaimTemplates": [{
    "metadata": {
      "name": "test-template-name",
      "namespace": "test"
    },
    "spec": {
      "accessModes": ["ReadWriteOnce"],
      "resources": {
        "requests": {
          "storage": "1Gi"
        }
      }
    },
    "mountPath": "/pulsar/temp"
  }]
}
    `,
  },
];

const logLabel = (
  <Tip
    content={
      <>
        <b>Log配置</b>
        <br />
        以log4j为例，你需要在configmap对应的key中配置完整的log4j2.xml格式文件配置，如果引用了configmap中不存在的配置，任务会启动失败。
      </>
    }
  >
    Log配置
  </Tip>
);

const initialData = {
  resourcesCpu: 1,
  limitCpu: 1,
  resourcesRam: 1073741824,
  limitRam: 1073741824,
  parallelism: 1,
  batchBuilder: 'DEFAULT',
  forwardSourceMessageProperty: true,
  maxPendingAsyncRequests: 1000,
  authConfig: {
    clientAuthenticationParameters: '',
    clientAuthenticationPlugin: 'org.apache.pulsar.client.impl.auth.AuthenticationToken',
  },
  pythonLogConfig: {
    name: '',
    key: '',
  },
  javaLogConfig: {
    name: '',
    key: '',
  },

  inputTypeClassName: '',
  outputTypeClassName: '',
};

// 启动
export default function StartManage({
  visible,
  diffNodes,
  handleClose,
  optionRequests,
  isEdit,
  form,
  clusterName,
  functionType,
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [options, setOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [extraConfigVisible, setExtraConfigVisible] = useState(false);
  const [authVisible, setAuthVisible] = useState(false);
  const [authData, setAuthData] = useState([]);
  const [isAuthed, setIsAuthed] = useState(false);
  const navigate = useNavigate();

  const retainKeyOrdering = Form.useWatch('retainKeyOrdering', form);

  const { getConfigDetail, getPackageList, updateFunctionConfig } = optionRequests;

  const handleDialogConfirm = (result) => {
    if (result) {
      setIsAuthed(true);
      form.submit();
    }
  };

  const handleDialogClose = () => {
    setAuthVisible(false);
    setIsAuthed(false);
  };

  const handleSubmit = async (values) => {
    const { othersK8s, ...others } = values;
    const parmas = {
      ...data,
      ...others,
    };
    // 拆分othersK8s参数
    if (othersK8s) {
      for (const [key, jsonStr] of Object.entries(JSON.parse(othersK8s || null)) || {}) {
        parmas[key] = jsonStr;
      }
    }
    // 特殊处理isWindowFunction
    if (!parmas.isWindowFunction) {
      parmas.slidingIntervalDurationMs = null;
      parmas.windowLengthDurationMs = null;
    }
    delete parmas.othersK8s;
    delete parmas.extraConfig;
    console.log(isAuthed);
    // 判断token权限检查
    if (!isAuthed) {
      try {
        setSubmitting(true);
        const res = await checkFunctionAuthRole({
          token: parmas.authConfig.clientAuthenticationParameters,
          clusterName,
          functionNamespace: `${parmas.tenant}/${parmas.namespace}`,
          packageName: parmas.packageURL,
          functionType,
          inputTopics: parmas.inputs ? parmas.inputs.split(',') : [],
          outputTopic: parmas.output ? parmas.output : parmas.destinationTopic,
        });
        setAuthData(res.data);
        setAuthVisible(true);
        return;
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        setSubmitting(false);
      }
    }
    setAuthVisible(false);
    setIsAuthed(false);
    // 开始重新组合结构
    const {
      authConfig,
      env = '',
      extraDependenciesDir = '',
      inputTypeClassName = '',
      javaLogConfig,
      jvmOptions = '',
      labels = '',
      nodeSelector = '',
      outputTypeClassName = '',
      podAffinity = '',
      pythonLogConfig,
      tolerations = '',
      functionSpecVolumeClaimTemplates = '',
      ...otherParams
    } = parmas;

    const updateParams = {
      ...otherParams,
      customRuntimeOptions: {
        authConfig: JSON.stringify(authConfig),
        env,
        extraDependenciesDir,
        inputTypeClassName,
        javaLogConfig: getLogConfigString(javaLogConfig),
        jvmOptions,
        labels,
        nodeSelector,
        outputTypeClassName,
        podAffinity: isVoid(podAffinity)
          ? ''
          : JSON.stringify({ podAffinity: JSON.parse(podAffinity) }),
        pythonLogConfig: getLogConfigString(pythonLogConfig),
        tolerations,
        functionSpecVolumeClaimTemplates,
      },
    };

    try {
      setSubmitting(true);

      await updateFunctionConfig(updateParams);
      MessagePlugin.success('操作成功');
      handleClose(true);
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const doInit = useMemoizedFn(async () => {
    try {
      setLoading(true);
      const { customRuntimeOptions, ...others } = await getConfigDetail();

      const {
        limitCpu,
        limitRam,
        authConfig,
        env,
        extraDependenciesDir,
        inputTypeClassName = '',
        javaLogConfig,
        jvmOptions,
        labels,
        nodeSelector,
        outputTypeClassName = '',
        podAffinity,
        pythonLogConfig,
        tolerations,
        functionSpecVolumeClaimTemplates,
      } = customRuntimeOptions || {};

      // k8s其他参数4项合并
      const data = {
        ...others,
        limitCpu,
        limitRam,
        inputTypeClassName,
        outputTypeClassName,
        authConfig: isVoid(authConfig) ? initialData.authConfig : JSON.parse(authConfig),
        env,
        extraDependenciesDir,
        jvmOptions,
        labels,
        pythonLogConfig: isVoid(pythonLogConfig)
          ? initialData.pythonLogConfig
          : JSON.parse(pythonLogConfig),
        javaLogConfig: isVoid(javaLogConfig)
          ? initialData.javaLogConfig
          : JSON.parse(javaLogConfig),
        othersK8s: JSON.stringify({
          podAffinity: isVoid(podAffinity)
            ? ''
            : JSON.stringify(JSON.parse(podAffinity).podAffinity, null, 2),
          nodeSelector,
          tolerations,
          functionSpecVolumeClaimTemplates,
        }),
      };
      loadOptions(data.runtime);
      setData(data);
      if (isEdit) {
        // 编辑时展开
        setExtraConfigVisible(true);
        form.setFieldsValue(data);
      } else {
        form.setFieldsValue(initialData);
      }
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setLoading(false);
    }
  });

  const loadOptions = useMemoizedFn(async (val) => {
    const { data } = await getPackageList(val);
    setOptions(
      data.map((item) => {
        const str = `${item.packageName}@${item.version}`;
        return { label: str, value: str };
      }),
    );
  });

  const keyOrderChange = (selected) => {
    if (selected) {
      form.setFieldsValue({
        batchBuilder: 'KEY_BASED',
      });
    }
  };

  useEffect(() => {
    if (visible) {
      doInit();
    } else {
      setExtraConfigVisible(false);
      form.resetFields();
    }
  }, [doInit, visible, form]);

  const header = React.cloneElement(diffNodes.startHeader, null, isEdit ? '配置' : '启动');

  return (
    <Drawer
      visible={visible}
      closeBtn
      header={
        <Line>
          {header}
          <YamlToJson />
        </Line>
      }
      size={1000}
      onClose={handleClose}
      placement='right'
      destroyOnClose
      onConfirm={() => form.submit()}
      confirmBtn={
        <DisableWrapper>
          <Button
            loading={submitting}
            type='primary'
            content='确定'
            onClick={() => form.submit()}
          />
        </DisableWrapper>
      }
    >
      <Loading loading={loading}>
        <Form form={form} labelAlign='right' labelWidth={120} onFinish={handleSubmit}>
          <Body className='narrow-scrollbar'>
            <Descriptions column={2} style={{ padding: '0 0 18px 26px' }}>
              <Descriptions.Item label='租户' value={data.tenant} />
              <Descriptions.Item label='命名空间' value={`${data.tenant}/${data.namespace}`} />
              <Descriptions.Item label='任务名' value={data.name} />
              <Descriptions.Item label='任务类型' value={data.runtime} />
              <Descriptions.Item label='备注' value={data.note} span={2} />
            </Descriptions>
            <Collapse
              borderless
              defaultValue={['runtime', 'auth', 'config']}
              expandIconPlacement='right'
              expandOnRowClick
            >
              <Panel header='运行参数' value={'runtime'}>
                <FormItem label={<Tip>CPU</Tip>}>
                  <Block>
                    <FormItem
                      labelWidth={90}
                      rules={[{ required: true }]}
                      label='request'
                      name='resourcesCpu'
                    >
                      <InputNumber />
                    </FormItem>
                    <FormItem
                      labelWidth={90}
                      rules={[{ required: true }]}
                      label='limit'
                      name='limitCpu'
                    >
                      <InputNumber />
                    </FormItem>
                  </Block>
                </FormItem>
                <FormItem label={<Tip>内存(byte)</Tip>}>
                  <Block>
                    <FormItem
                      labelWidth={90}
                      rules={[{ required: true }]}
                      label='request'
                      name='resourcesRam'
                    >
                      <InputNumber theme='normal' />
                    </FormItem>
                    <FormItem
                      labelWidth={90}
                      rules={[{ required: true }]}
                      label='limit'
                      name='limitRam'
                    >
                      <InputNumber theme='normal' />
                    </FormItem>
                  </Block>
                </FormItem>

                <FormItem rules={[{ required: true }]} name='parallelism' label='实例个数'>
                  <InputNumber />
                </FormItem>
              </Panel>
              <Panel header='任务配置' value={'config'}>
                <FormItem label='包名' name='packageURL' rules={[{ required: true }]}>
                  <Select options={toTsOptions(options)} />
                </FormItem>
                <TipContainer>
                  没找到，请到
                  <Link
                    onClick={() => {
                      navigate(`/cluster/function-packageManage/list?name=${clusterName}`);
                    }}
                    theme='primary'
                    hover='color'
                    size='small'
                  >
                    包管理列表
                  </Link>
                  上传
                </TipContainer>
                {diffNodes.startFormSnipPart1}
                <FormItem
                  name='retainKeyOrdering'
                  label={
                    <Tip
                      content={
                        <>
                          <strong>是否有序</strong>
                          <li>1、是，代表采用key_shared模式消费数据源。</li>
                          <li>2、否，代表采用Shared模式消费数据源。</li>
                          <li>该参数不支持更新！</li>
                        </>
                      }
                    >
                      是否有序
                    </Tip>
                  }
                  tips={isEdit && '该参数不支持更新！'}
                >
                  <Switch onChange={keyOrderChange} disabled={isEdit} />
                </FormItem>
                <FormItem
                  name='batchBuilder'
                  label={
                    <Tip
                      content={
                        <>
                          <strong>批量策略</strong>
                          <li>1、往下游进行批量生产时，采用的批量策略。</li>
                          <li>2、KEY_BASED代表相同的消息KEY组装为一批消息。</li>
                          <li>3、DEFAULT代表不考虑消息KEY组装为一批消息。</li>
                        </>
                      }
                    >
                      批量策略
                    </Tip>
                  }
                >
                  <Select
                    disabled={retainKeyOrdering}
                    options={toTsOptions([
                      {
                        value: 'KEY_BASED',
                        label: 'KEY_BASED',
                      },
                      {
                        value: 'DEFAULT',
                        label: 'DEFAULT',
                      },
                    ])}
                  />
                </FormItem>

                {diffNodes.startFormSnipPart2}

                <Title onClick={() => setExtraConfigVisible((vis) => !vis)}>
                  高级配置 <Icon type='chevron-double-up' rotate={extraConfigVisible ? 0 : 180} />
                </Title>

                <div style={{ display: extraConfigVisible ? 'block' : 'none' }}>
                  {diffNodes.startFormSnipPart3}
                </div>
              </Panel>
              <Panel header='权限配置' value={'auth'}>
                <FormItem
                  label={
                    <Tip
                      content={
                        <>
                          <b>function 任务运行时的pulsar权限配置</b>
                          <br />
                          1.该token需要pulsar集群有输入输出topic的
                          <Tag
                            style={{ margin: '0 4px' }}
                            theme='primary'
                            size='small'
                            variant='light'
                          >
                            produce
                          </Tag>
                          ，
                          <Tag
                            style={{ margin: '0 4px' }}
                            theme='primary'
                            size='small'
                            variant='light'
                          >
                            consume
                          </Tag>
                          权限，function所在namespace的
                          <Tag
                            style={{ margin: '0 4px' }}
                            theme='primary'
                            size='small'
                            variant='light'
                          >
                            functions
                          </Tag>
                          权限，以及package所在namespace的
                          <Tag
                            style={{ margin: '0 4px' }}
                            theme='primary'
                            size='small'
                            variant='light'
                          >
                            packages
                          </Tag>
                          权限。
                          <br />
                          2.若pulsar集群没有开始认证可以不配置。
                          <br />
                          3.不填写，默认使用admin权限。
                        </>
                      }
                    >
                      Token权限配置
                    </Tip>
                  }
                >
                  <Block>
                    <FormItem
                      labelWidth={250}
                      label='clientAuthenticationParameters'
                      name={['authConfig', 'clientAuthenticationParameters']}
                    >
                      <Input placeholder='请输入你的token' />
                    </FormItem>
                    <FormItem
                      labelWidth={250}
                      label='clientAuthenticationPlugin'
                      name={['authConfig', 'clientAuthenticationPlugin']}
                    >
                      <Text />
                    </FormItem>
                  </Block>
                </FormItem>
              </Panel>
              <Panel header='K8S运行时参数' value={'k8s-advanced'}>
                <FormItem
                  name='labels'
                  label={<Tip content='Pod 的标签配置'>标签</Tip>}
                  rules={[
                    {
                      validator: isValidSimpleJSONRC,
                      message: '请输入[key, value]结构的json格式，不允许嵌套',
                    },
                  ]}
                >
                  <JSONConverterInput
                    placeholder={`参考示例如下
{
  "key1": "value1",
  "key2": "value2",
  "...": "...."
}     
                  `}
                  />
                </FormItem>

                <FormItem
                  name='env'
                  label={<Tip content='Pod 的环境变量配置'>环境变量</Tip>}
                  rules={[
                    {
                      validator: isValidSimpleJSONRC,
                      message: '请输入[key, value]结构的json格式，不允许嵌套',
                    },
                  ]}
                >
                  <JSONConverterInput
                    placeholder={`参考示例如下
{
  "LANG": "C.UTF-8",
  "LC_ALL": "C.UTF-8",
  "TZ": "Asia/Shanghai"，
    "...": "...."
}      
                  `}
                  />
                </FormItem>

                <FormItem
                  name='othersK8s'
                  label='k8s其他参数'
                  rules={[{ validator: isValidSubJSONRC, message: '请输入json格式' }]}
                >
                  <JSONsInput tabs={othersK8sTabs} />
                </FormItem>
              </Panel>
              {data.runtime?.toLowerCase() === 'java' && (
                <Panel header='JAVA运行时参数' value={'java-advanced'}>
                  <FormItem
                    name='jvmOptions'
                    label={
                      <Tip content='JAVA启动参数 配置。可以在这里指定内存大小环境变量等'>
                        启动参数
                      </Tip>
                    }
                    rules={[{ validator: isValidJSONRC, message: '请输入json格式' }]}
                  >
                    <JSONConverterInput
                      jsonOnly
                      placeholder={`示例如下：
{
  "javaOPTs": 
  ["-Dasp_env=prod",
  "-Dappid=1",
  "-Dtopic=ASP-Metrics-topic",
  "-cp /pulsar/instances/java-instance.jar:random-dir/*:/pulsar/pulsar-function-sub-3.1-SNAPSHOT.jar"
  ]
}          
                    `}
                    />
                  </FormItem>
                  <FormItem label={logLabel}>
                    <Block>
                      <FormItem name={['javaLogConfig', 'name']} label='name'>
                        <Input />
                      </FormItem>
                      <FormItem name={['javaLogConfig', 'key']} label='key'>
                        <Input />
                      </FormItem>
                    </Block>
                  </FormItem>
                  <FormItem
                    label={
                      <Tip
                        content={
                          <>
                            额外依赖目录extraDependenciesDir
                            <br />
                            比如Java中需要依赖第三方Jar包
                          </>
                        }
                      >
                        额外依赖目录
                      </Tip>
                    }
                    name='extraDependenciesDir'
                  >
                    <Input placeholder='如：/pulsar/instances/deps' />
                  </FormItem>
                  {diffNodes.startFormSnipJava}
                </Panel>
              )}
              {data.runtime?.toLowerCase() === 'python' && (
                <Panel header='PYTHON运行时参数' value={'python-advanced'}>
                  <FormItem label={logLabel}>
                    <Block>
                      <FormItem name={['pythonLogConfig', 'name']} label='name'>
                        <Input />
                      </FormItem>
                      <FormItem name={['pythonLogConfig', 'key']} label='key'>
                        <Input />
                      </FormItem>
                    </Block>
                  </FormItem>
                </Panel>
              )}
            </Collapse>
          </Body>
        </Form>
      </Loading>
      <Auth
        authVisible={authVisible}
        authData={authData}
        onConfirm={handleDialogConfirm}
        onClose={handleDialogClose}
        clusterName={clusterName}
      />
    </Drawer>
  );
}

const Body = styled.div`
  padding-bottom: 30px;
  box-sizing: border-box;
  overflow-y: auto;
  .t-form__controls.t-is-success .t-input__extra {
    color: var(--td-text-color-placeholder);
  }

  .t-collapse-panel__header {
    background-color: #fafafa;
    position: relative;
    height: 38px;
    box-sizing: border-box;
    &::before {
      content: ' ';
      position: absolute;
      width: 3px;
      height: 20px;
      left: 0;
      top: 0;
      bottom: 0;
      margin: auto;
      background: rgb(25, 77, 255);
    }
    .t-collapse-panel__icon {
      color: rgba(32, 43, 64, 0.5);
      .t-fake-arrow {
        transform: rotate(-180deg);
      }
      &::before {
        content: '收起';
        font-size: 14px;
        font-weight: normal;
        margin-right: 2px;
      }
      &.t-collapse-panel__icon--active {
        .t-fake-arrow {
          transform: rotate(0deg);
        }
        &::before {
          content: '展开';
        }
      }
    }
  }
  .t-collapse-panel__content {
    padding-top: var(--td-comp-paddingTB-xl);
    padding-bottom: var(--td-comp-paddingTB-xl);
  }
`;

const TipContainer = styled.div`
  margin-bottom: 12px;
  margin-left: 100px;
  margin-top: -12px;
  font-size: 12px;
  color: var(--td-text-color-secondary);
`;

const Line = styled.div`
  display: flex;
  flex: 1;
  padding-right: 30px;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div`
  font-weight: bold;
  font-size: 14px;
  color: var(--td-brand-color-7);
  padding: 0 0 20px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;

const Icon = styled(SvgIcon)`
  transition: 0.4s all cubic-bezier(0.645, 0.045, 0.355, 1);
  transform: rotate(${(props) => props.rotate}deg);
  margin-left: 4px;
`;

const Block = styled.div`
  width: 100%;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.05);
  z-index: 1;
  border-radius: 6px;
  background-color: #fafafa;
  padding: 16px 30px 16px 16px;
  .t-form__item {
    margin-bottom: 10px;
    margin-right: 0;
  }
`;
